import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Shield, FileText, Menu, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

export function Logo({ size = "md" }) {
  const cls = size === "lg" ? "text-2xl" : "text-lg";
  return (
    <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 font-display font-black tracking-tight text-slate-900 transition-lift">
      <img src="/brand/fmg-icon.png" alt="FileMyGrievance" className="h-9 w-9 rounded-md object-cover" />
      <span className={cls}><span className="text-slate-900">File</span><span className="text-red-600">My</span><span className="text-slate-900">Grievance</span></span>
    </Link>
  );
}

const PUBLIC_LINKS = [
  { to:"/how-it-works", label:"How It Works" },
  { to:"/supported-contracts", label:"Supported Contracts" },
  { to:"/pricing", label:"Plans" },
  { to:"/why", label:"Why This Exists" },
  { to:"/faq", label:"FAQ" },
  { to:"/contact", label:"Contact" },
];

const MEMBER_LINKS = [
  { to:"/dashboard", label:"Dashboard" },
  { to:"/new-grievance", label:"New Grievance" },
  { to:"/incidents", label:"Incidents" },
  { to:"/contract-library", label:"Contract Library" },
  { to:"/notifications", label:"Notifications" },
  { to:"/billing", label:"Billing" },
  { to:"/profile", label:"Profile" },
];

const ADVOCATE_LINK = { to:"/profile#advocate-program", label:"Advocate Program" };

export function PublicHeader() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = React.useState(false);
  const doLogout = () => { logout(); setOpen(false); nav("/"); };
  const links = PUBLIC_LINKS;
  const isMember = user && user.role !== "admin" && user.role !== "steward";
  return (
    <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Logo />
        <nav className="hidden md:flex items-center gap-6">
          {links.map(l => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g,'-')}`}
              className={({isActive}) => `text-sm font-medium ${isActive?"text-red-600":"text-slate-700 hover:text-slate-900"}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <>
              <Link to={user.role==='admin'?'/admin':user.role==='steward'?'/steward-dashboard':'/dashboard'}>
                <Button data-testid="nav-dashboard" className="bg-slate-900 hover:bg-slate-800 text-white">Go to Dashboard</Button>
              </Link>
              <button type="button" onClick={doLogout} data-testid="nav-logout" className="text-sm font-medium text-slate-700 hover:text-slate-900 ml-1">Logout</button>
            </>
          ) : (
            <>
              <Link to="/login"><Button data-testid="nav-login" variant="ghost" className="text-slate-700">Login</Button></Link>
              <Link to="/register"><Button data-testid="nav-start" className="bg-red-600 hover:bg-red-700 text-white">Start My Grievance</Button></Link>
            </>
          )}
        </div>
        <button className="md:hidden" onClick={()=>setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
          {open ? <X className="h-6 w-6"/> : <Menu className="h-6 w-6"/>}
        </button>
      </div>
      {open && (
        <div className="md:hidden bg-white border-t border-slate-200 px-4 py-3 space-y-2">
          {isMember && (
            <div className="pb-2 border-b border-slate-200" data-testid="mobile-member-nav">
              {[...MEMBER_LINKS, ADVOCATE_LINK].map(l => (
                <Link key={l.to} to={l.to} className="block py-2 font-medium text-slate-900" onClick={()=>setOpen(false)}
                      data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g,'-')}`}>{l.label}</Link>
              ))}
            </div>
          )}
          {links.map(l => <Link key={l.to} to={l.to} className="block py-2 text-slate-700" onClick={()=>setOpen(false)}>{l.label}</Link>)}
          {!user && <div className="pt-2 flex gap-2">
            <Link className="flex-1" to="/login"><Button variant="outline" className="w-full">Login</Button></Link>
            <Link className="flex-1" to="/register"><Button className="w-full bg-red-600 hover:bg-red-700 text-white">Sign Up</Button></Link>
          </div>}
          {user && <div className="pt-2 flex gap-2">
            <Link className="flex-1" to={user.role==='admin'?'/admin':user.role==='steward'?'/steward-dashboard':'/dashboard'} onClick={()=>setOpen(false)}><Button className="w-full bg-slate-900 hover:bg-slate-800 text-white">Go to Dashboard</Button></Link>
            <Button variant="outline" onClick={doLogout} data-testid="nav-logout-mobile" className="flex-1">Logout</Button>
          </div>}
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="bg-white border-t border-slate-200 mt-24">
      <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-1 md:grid-cols-4 gap-8">
        <div>
          <Logo />
          <p className="text-sm text-slate-500 mt-3 max-w-xs">Turn workplace problems into organized grievances.</p>
        </div>
        <div>
          <h4 className="font-semibold text-slate-900 mb-3">Platform</h4>
          <ul className="text-sm space-y-2 text-slate-600">
            <li><Link to="/how-it-works">How It Works</Link></li>
            <li><Link to="/pricing">Plans</Link></li>
            <li><Link to="/supported-contracts">Supported Contracts</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold text-slate-900 mb-3">Company</h4>
          <ul className="text-sm space-y-2 text-slate-600">
            <li><Link to="/why">Why This Exists</Link></li>
            <li><Link to="/faq">FAQ</Link></li>
            <li><Link to="/contact">Contact</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold text-slate-900 mb-3">Legal</h4>
          <ul className="text-sm space-y-2 text-slate-600">
            <li><Link to="/terms">Terms of Use</Link></li>
            <li><Link to="/privacy">Privacy Policy</Link></li>
            <li><Link to="/disclaimer">Disclaimer</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-200 py-4 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} FileMyGrievance.com — Not a law firm. Not a union. Independent document-preparation platform.
      </div>
    </footer>
  );
}

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isSteward = user?.role === "steward";
  const isAdmin = user?.role === "admin";
  const links = isAdmin
    ? [
        { to:"/admin", label:"Admin Dashboard" },
        { to:"/admin/users", label:"Users" },
        { to:"/admin/cases", label:"Cases" },
        { to:"/admin/articles", label:"Contract Articles" },
        { to:"/admin/categories", label:"Categories" },
        { to:"/admin/messages", label:"Contact Messages" },
        { to:"/contact", label:"Contact Us" },
      ]
    : isSteward
    ? [
        { to:"/dashboard", label:"Employee Dashboard" },
        { to:"/steward-dashboard", label:"Steward Dashboard" },
        { to:"/steward-meetings", label:"Meetings" },
        { to:"/notifications", label:"Notifications" },
        { to:"/profile", label:"Profile" },
        { to:"/contact", label:"Contact Us" },
      ]
    : [
        ...MEMBER_LINKS,
        { to:"/contact", label:"Contact Us" },
      ];
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const isMember = !isAdmin && !isSteward;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Logo />
            {user?.plan && (
              <span data-testid="plan-badge" className={`hidden sm:inline-block text-xs font-semibold px-2 py-1 rounded ${user.plan==='premium'?'bg-red-50 text-red-700 border border-red-200':'bg-slate-100 text-slate-700 border border-slate-200'}`}>
                {user.plan.toUpperCase()}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-slate-600" data-testid="user-name">{user?.first_name} {user?.last_name}</span>
            <Button data-testid="logout-btn" variant="outline" size="sm" onClick={()=>{ logout(); nav("/"); }}>Logout</Button>
            <button className="md:hidden" onClick={()=>setMobileOpen(!mobileOpen)} data-testid="app-mobile-menu-toggle" aria-label="Menu">
              {mobileOpen ? <X className="h-6 w-6"/> : <Menu className="h-6 w-6"/>}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-slate-200 px-4 py-3" data-testid="app-mobile-menu">
            {user?.plan && (
              <div className="flex items-center justify-between pb-2 mb-2 border-b border-slate-200">
                <span className="text-sm font-medium text-slate-700">{user.first_name} {user.last_name}</span>
                <span data-testid="mobile-plan-badge" className={`text-xs font-semibold px-2 py-1 rounded ${user.plan==='premium'?'bg-red-50 text-red-700 border border-red-200':'bg-slate-100 text-slate-700 border border-slate-200'}`}>
                  {user.plan.toUpperCase()}
                </span>
              </div>
            )}
            <div className="pb-2 border-b border-slate-200">
              {(isMember ? [...links.filter(l=>l.to!=="/contact"), ADVOCATE_LINK] : links).map(l => (
                <Link key={l.to} to={l.to} className="block py-2 font-medium text-slate-900" onClick={()=>setMobileOpen(false)}
                      data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g,'-')}`}>{l.label}</Link>
              ))}
            </div>
            <div className="pt-2">
              {PUBLIC_LINKS.map(l => (
                <Link key={l.to} to={l.to} className="block py-2 text-slate-600" onClick={()=>setMobileOpen(false)}>{l.label}</Link>
              ))}
            </div>
            <div className="pt-2 border-t border-slate-200">
              <Button variant="outline" data-testid="mobile-logout-btn" className="w-full" onClick={()=>{ logout(); nav("/"); }}>Logout</Button>
            </div>
          </div>
        )}
      </header>
      <div className="flex-1 flex max-w-7xl mx-auto w-full">
        <aside className="hidden md:block w-60 py-8 pr-4 border-r border-slate-200">
          <nav className="space-y-1">
            {links.map(l=>(
              <NavLink key={l.to} to={l.to} end
                data-testid={`sidebar-${l.label.toLowerCase().replace(/\s+/g,'-')}`}
                className={({isActive}) => `block px-3 py-2 rounded-md text-sm font-medium transition-lift ${isActive?'bg-red-50 text-red-700 border border-red-100':'text-slate-700 hover:bg-slate-100'}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-8">{children}</main>
      </div>
    </div>
  );
}
